import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	Grid,
	MenuItem,
	Paper,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tab,
	Tabs,
	TextField,
	Typography,
} from '@mui/material';
import SectionTitle from '../../components/common/SectionTitle';
import OrbitAiAdminService from '../../services/OrbitAiAdminService';
import GlobalTip from '../../providers/GlobalTip';

const STATUS_COLOR_MAP = {
	aborted: 'default',
	queued: 'info',
	pending: 'warning',
	failed: 'error',
	completed: 'success',
};

const DAG_PHASE_COLOR_MAP = {
	queued: 'info',
	running: 'warning',
	completed: 'success',
	failed: 'error',
	not_found: 'default',
	unavailable: 'default',
	not_applicable: 'default',
	unknown: 'default',
};

const OPERATOR_STATE_COLOR_MAP = {
	aborted: 'default',
	in_progress: 'warning',
	terminal_failure: 'error',
	completed: 'success',
	queued: 'info',
	pending: 'warning',
	unknown: 'default',
};

const SUMMARY_CARD_ORDER = ['aborted', 'failed', 'pending', 'queued', 'completed'];
const DETAIL_AUTO_REFRESH_MS = 15000;
const LEGACY_FORECAST_RUNS_ROUTE = '/admin/orbit-ai/forecast-runs';
const LEGACY_TOKEN_USAGE_ROUTE = '/admin/orbit-ai/token-usage';
const LEGACY_MAINTENANCE_ROUTE = '/admin/orbit-ai/maintenance';
const V3_FORECAST_RUNS_ROUTE = '/v3/admin/orbit-ai/forecast-runs';
const V3_TOKEN_USAGE_ROUTE = '/v3/admin/orbit-ai/token-usage';
const V3_MAINTENANCE_ROUTE = '/v3/admin/orbit-ai/maintenance';

function getActiveTabFromPathname(pathname) {
	if (String(pathname || '').includes('/maintenance')) {
		return 'maintenance';
	}

	return String(pathname || '').includes('/token-usage') ? 'token-usage' : 'forecast-runs';
}

function getOrbitAiAdminBasePaths(pathname) {
	const isV3Route = String(pathname || '').startsWith('/v3/')
	return {
		forecastRunsRoute: isV3Route ? V3_FORECAST_RUNS_ROUTE : LEGACY_FORECAST_RUNS_ROUTE,
		tokenUsageRoute: isV3Route ? V3_TOKEN_USAGE_ROUTE : LEGACY_TOKEN_USAGE_ROUTE,
		maintenanceRoute: isV3Route ? V3_MAINTENANCE_ROUTE : LEGACY_MAINTENANCE_ROUTE,
	}
}

function formatDateTime(value) {
	if (!value) {
		return '—';
	}

	if (typeof value === 'object') {
		return toDisplayText(value);
	}

	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) {
		return toDisplayText(value);
	}

	return parsed.toLocaleString();
}

function formatInteger(value) {
	if (value === null || value === undefined || value === '') {
		return '0';
	}

	const parsed = Number(value);
	if (Number.isNaN(parsed)) {
		return String(value);
	}

	return parsed.toLocaleString();
}

function truncateText(value, maxLength = 140) {
	if (!value) {
		return '—';
	}

	const normalized = String(value);
	if (normalized.length <= maxLength) {
		return normalized;
	}

	return `${normalized.slice(0, maxLength)}…`;
}

function toDisplayText(value, fallback = '—') {
	if (value === null || value === undefined) {
		return fallback;
	}

	if (typeof value === 'string') {
		const normalized = value.trim();
		return normalized === '' ? fallback : normalized;
	}

	if (typeof value === 'number' || typeof value === 'boolean') {
		return String(value);
	}

	if (Array.isArray(value)) {
		const normalizedItems = value
			.map((item) => toDisplayText(item, ''))
			.filter((item) => item !== '');
		return normalizedItems.length > 0 ? normalizedItems.join(', ') : fallback;
	}

	if (typeof value === 'object') {
		try {
			const serialized = JSON.stringify(value);
			if (!serialized || serialized === '{}' || serialized === '[]') {
				return fallback;
			}
			return serialized;
		} catch (_error) {
			return fallback;
		}
	}

	return String(value);
}

function getLatestErrorText(value) {
	return toDisplayText(value?.latest_error_message, '') || toDisplayText(value?.last_error, '') || '—';
}

function toJsonPreviewText(value) {
	if (value === null || value === undefined || value === '') {
		return '';
	}

	if (typeof value === 'string') {
		return value;
	}

	if (typeof value === 'object') {
		try {
			return JSON.stringify(value, null, 2);
		} catch (_error) {
			return toDisplayText(value, '');
		}
	}

	return String(value);
}

function formatScope(run) {
	if (run?.trigger_scope === 'location_class') {
		return `Location Class${run?.requested_location_class_id ? ` #${run.requested_location_class_id}` : ''}`;
	}

	return 'Client';
}

function getRunStatusDetailText(run) {
	if (!run) {
		return '';
	}

	if (run.is_operator_nuclear_reset || run.effective_status === 'aborted') {
		return 'This run was aborted by an operator nuclear reset. No replacement work was queued for this cleared request.';
	}

	if (toDisplayText(run.status_detail_message, '') !== '') {
		return run.status_detail_message;
	}

	if (run.is_running_with_errors) {
		return `This run is still active. ${formatInteger(run.failed_task_count || 0)} forecast task(s) have failed so far, ${formatInteger(run.pending_task_count || 0)} task(s) remain pending, and ${formatInteger(run.result_count || 0)} result row(s) have been written.`;
	}

	return '';
}

function getOperatorState(run) {
	if (!run) {
		return { key: 'unknown', label: 'Unknown' };
	}

	if (run.is_operator_nuclear_reset || run.effective_status === 'aborted') {
		return { key: 'aborted', label: 'Aborted' };
	}

	if (run.effective_status === 'failed') {
		return { key: 'terminal_failure', label: 'Terminal Failure' };
	}

	if (run.is_running_with_errors || (run.registry_status === 'RUNNING' && run.has_outstanding_work)) {
		return { key: 'in_progress', label: 'In Progress' };
	}

	if (run.effective_status === 'completed') {
		return { key: 'completed', label: 'Completed' };
	}

	if (run.effective_status === 'queued') {
		return { key: 'queued', label: 'Queued' };
	}

	if (run.effective_status === 'pending') {
		return { key: 'pending', label: 'Pending' };
	}

	return { key: 'unknown', label: 'Unknown' };
}

function JsonPreview({ label, value }) {
	const previewText = toJsonPreviewText(value);

	if (!previewText) {
		return null;
	}

	return (
		<Box sx={{ mt: 1.5 }}>
			<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
				{label}
			</Typography>
			<Box
				component="pre"
				sx={{
					m: 0,
					p: 1.5,
					fontSize: 12,
					whiteSpace: 'pre-wrap',
					wordBreak: 'break-word',
					borderRadius: 1,
					backgroundColor: '#111827',
					color: '#F9FAFB',
					maxHeight: 220,
					overflow: 'auto',
				}}
			>
				{previewText}
			</Box>
		</Box>
	);
}

function SummaryCard({ label, value, color = 'default' }) {
	return (
		<Paper elevation={1} sx={{ p: 2, borderRadius: 2, minHeight: 94 }}>
			<Typography variant="body2" color="text.secondary">
				{label}
			</Typography>
			<Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
				<Typography variant="h4" sx={{ lineHeight: 1 }}>
					{value}
				</Typography>
				{color !== 'default' ? <Chip size="small" color={color} label={label} /> : null}
			</Box>
		</Paper>
	);
}

function RunStat({ label, value, color = 'text.primary' }) {
	const displayValue = React.isValidElement(value) ? value : toDisplayText(value);

	return (
		<Box>
			<Typography variant="caption" color="text.secondary">
				{label}
			</Typography>
			{React.isValidElement(displayValue) ? (
				<Box sx={{ mt: 0.5 }}>
					{displayValue}
				</Box>
			) : (
				<Typography variant="body2" sx={{ color, fontWeight: 600 }}>
					{displayValue}
				</Typography>
			)}
		</Box>
	);
}

function OrbitAiAdmin() {
	const tip = GlobalTip.useMessage();
	const location = useLocation();
	const navigate = useNavigate();
	const { triggerRequestId } = useParams();
	const { forecastRunsRoute, tokenUsageRoute, maintenanceRoute } = useMemo(
		() => getOrbitAiAdminBasePaths(location.pathname),
		[location.pathname],
	)
	const [runs, setRuns] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [loadError, setLoadError] = useState('');
	const [clientCodeFilter, setClientCodeFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState('all');
	const [limit, setLimit] = useState(100);
	const [detailOpen, setDetailOpen] = useState(false);
	const [selectedRunSummary, setSelectedRunSummary] = useState(null);
	const [selectedRunDetail, setSelectedRunDetail] = useState(null);
	const [detailLoading, setDetailLoading] = useState(false);
	const [detailError, setDetailError] = useState('');
	const [detailLastCheckedAt, setDetailLastCheckedAt] = useState(null);
	const [resubmittingRunId, setResubmittingRunId] = useState(null);
	const [retryingDagRunId, setRetryingDagRunId] = useState(null);
	const [reconcilingRunId, setReconcilingRunId] = useState(null);
	const [nuclearResettingRunId, setNuclearResettingRunId] = useState(null);
	const [activeTab, setActiveTab] = useState(() => getActiveTabFromPathname(location.pathname));
	const [tokenUsageReport, setTokenUsageReport] = useState(null);
	const [isTokenUsageLoading, setIsTokenUsageLoading] = useState(false);
	const [tokenUsageError, setTokenUsageError] = useState('');
	const [tokenUsageDays, setTokenUsageDays] = useState(30);
	const [tokenUsageDrilldown, setTokenUsageDrilldown] = useState(null);
	const [isTokenUsageDrilldownLoading, setIsTokenUsageDrilldownLoading] = useState(false);
	const [tokenUsageDrilldownError, setTokenUsageDrilldownError] = useState('');
	const [tokenUsageDrilldownServiceKey, setTokenUsageDrilldownServiceKey] = useState('daily_forecasting');
	const [tokenUsageDrilldownGranularity, setTokenUsageDrilldownGranularity] = useState('client');
	const [tokenUsageDrilldownLimit, setTokenUsageDrilldownLimit] = useState(50);
	const [maintenanceConfig, setMaintenanceConfig] = useState({
		is_enabled: false,
		message: '',
		updated_at: null,
		updated_by_user_id: null,
	});
	const [maintenanceLoading, setMaintenanceLoading] = useState(false);
	const [maintenanceSaving, setMaintenanceSaving] = useState(false);
	const [maintenanceError, setMaintenanceError] = useState('');
	const detailRequestTokenRef = useRef(0);

	useEffect(() => {
		document.title = 'Retail ORBIT: Orbit AI Admin';
		loadRuns();
		loadTokenUsage();
		loadTokenUsageDrilldown();
		loadMaintenanceMessage();
	}, []);

	useEffect(() => {
		setActiveTab(getActiveTabFromPathname(location.pathname));
	}, [location.pathname]);

	const filteredRuns = useMemo(() => {
		return runs.filter((run) => {
			const matchesStatus = statusFilter === 'all' || run.effective_status === statusFilter;
			const normalizedClientCodeFilter = clientCodeFilter.trim().toLowerCase();
			const matchesClientCode = normalizedClientCodeFilter === ''
				|| String(run.client_code || '').toLowerCase().includes(normalizedClientCodeFilter);
			return matchesStatus && matchesClientCode;
		});
	}, [clientCodeFilter, runs, statusFilter]);

	const summaryCounts = useMemo(() => {
		return SUMMARY_CARD_ORDER.reduce((accumulator, status) => {
			accumulator[status] = filteredRuns.filter((run) => run.effective_status === status).length;
			return accumulator;
		}, {});
	}, [filteredRuns]);

	async function loadRuns(nextOverrides = {}, options = {}) {
		const { showLoading = true, syncSelectedRunId = null } = options;

		if (showLoading) {
			setIsLoading(true);
		}
		setLoadError('');

		try {
			const response = await OrbitAiAdminService.listForecastRuns({
				client_code: (nextOverrides.clientCode ?? clientCodeFilter).trim() || undefined,
				limit: nextOverrides.limit ?? limit,
			});
			const nextRuns = response || [];
			setRuns(nextRuns);

			if (syncSelectedRunId) {
				const refreshedSelectedRun = nextRuns.find((run) => run.trigger_request_id === syncSelectedRunId);
				if (refreshedSelectedRun) {
					setSelectedRunSummary((previousValue) => {
						if (previousValue?.trigger_request_id !== syncSelectedRunId) {
							return previousValue;
						}

						return {
							...previousValue,
							...refreshedSelectedRun,
						};
					});
				}
			}
		} catch (error) {
			console.log(error);
			const message = error?.response?.data?.message || 'Unable to load Orbit AI Admin forecast runs.';
			setLoadError(message);
		} finally {
			if (showLoading) {
				setIsLoading(false);
			}
		}
	}

	async function loadTokenUsage(nextOverrides = {}) {
		setIsTokenUsageLoading(true);
		setTokenUsageError('');

		const days = Number(nextOverrides.days ?? tokenUsageDays);

		try {
			const response = await OrbitAiAdminService.getTokenUsage({ days });
			setTokenUsageReport(response || null);
		} catch (error) {
			console.log(error);
			const message = error?.response?.data?.message || 'Unable to load Orbit AI token usage.';
			setTokenUsageError(message);
		} finally {
			setIsTokenUsageLoading(false);
		}
	}

	async function loadTokenUsageDrilldown(nextOverrides = {}) {
		setIsTokenUsageDrilldownLoading(true);
		setTokenUsageDrilldownError('');

		const days = Number(nextOverrides.days ?? tokenUsageDays);
		const service_key = nextOverrides.service_key ?? tokenUsageDrilldownServiceKey;
		const granularity = nextOverrides.granularity ?? tokenUsageDrilldownGranularity;
		const limit = Number(nextOverrides.limit ?? tokenUsageDrilldownLimit);

		try {
			const response = await OrbitAiAdminService.getTokenUsageDrilldown({
				days,
				service_key,
				granularity,
				limit,
			});
			setTokenUsageDrilldown(response || null);
		} catch (error) {
			console.log(error);
			const message = error?.response?.data?.message || 'Unable to load Orbit AI token usage drilldown.';
			setTokenUsageDrilldownError(message);
		} finally {
			setIsTokenUsageDrilldownLoading(false);
		}
	}

	async function loadMaintenanceMessage() {
		setMaintenanceLoading(true)
		setMaintenanceError('')

		try {
			const response = await OrbitAiAdminService.getMaintenanceMessage()
			setMaintenanceConfig({
				is_enabled: response?.is_enabled === true,
				message: response?.message || '',
				updated_at: response?.updated_at || null,
				updated_by_user_id: response?.updated_by_user_id || null,
			})
		} catch (error) {
			console.log(error)
			setMaintenanceError(error?.response?.data?.message || 'Unable to load the Ask Indie maintenance banner.')
		} finally {
			setMaintenanceLoading(false)
		}
	}

	async function handleSaveMaintenanceMessage() {
		setMaintenanceSaving(true)
		setMaintenanceError('')

		try {
			const response = await OrbitAiAdminService.updateMaintenanceMessage({
				is_enabled: maintenanceConfig.is_enabled,
				message: maintenanceConfig.message,
			})
			setMaintenanceConfig({
				is_enabled: response?.is_enabled === true,
				message: response?.message || '',
				updated_at: response?.updated_at || null,
				updated_by_user_id: response?.updated_by_user_id || null,
			})
			tip.showSuccess(response?.is_enabled ? 'Ask Indie maintenance banner enabled.' : 'Ask Indie maintenance banner updated.')
		} catch (error) {
			console.log(error)
			setMaintenanceError(error?.response?.data?.message || 'Unable to update the Ask Indie maintenance banner.')
			tip.clear()
			tip.show(error?.response?.data?.message || 'Unable to update the Ask Indie maintenance banner.', 5)
		} finally {
			setMaintenanceSaving(false)
		}
	}

	async function fetchRunDetail(triggerRequestId, options = {}) {
		if (!triggerRequestId) {
			return;
		}

		const requestToken = detailRequestTokenRef.current + 1;
		detailRequestTokenRef.current = requestToken;
		const { showLoading = false } = options;
		if (showLoading) {
			setDetailLoading(true);
		}
		setDetailError('');

		try {
			const detail = await OrbitAiAdminService.getForecastRunDetail(triggerRequestId);
			if (detailRequestTokenRef.current !== requestToken) {
				return null;
			}
			setSelectedRunDetail(detail);
			setSelectedRunSummary((previousValue) => detail?.run || previousValue);
			setDetailLastCheckedAt(new Date().toISOString());
			return detail;
		} catch (error) {
			if (detailRequestTokenRef.current !== requestToken) {
				return null;
			}
			console.log(error);
			setDetailError(error?.response?.data?.message || 'Unable to load this forecast run detail.');
			return null;
		} finally {
			if (showLoading && detailRequestTokenRef.current === requestToken) {
				setDetailLoading(false);
			}
		}
	}

	async function openRunDetail(run) {
		if (!run?.trigger_request_id) {
			return;
		}

		setSelectedRunSummary(run);
		navigate(`${forecastRunsRoute}/${encodeURIComponent(run.trigger_request_id)}`);
	}

	function closeRunDetail() {
		setDetailOpen(false);
		setSelectedRunSummary(null);
		setSelectedRunDetail(null);
		setDetailError('');
		setDetailLastCheckedAt(null);
		navigate(forecastRunsRoute);
	}

	useEffect(() => {
		if (activeTab !== 'forecast-runs') {
			setDetailOpen(false);
			return;
		}

		if (!triggerRequestId) {
			setDetailOpen(false);
			return;
		}

		setSelectedRunSummary((previousValue) => {
			if (previousValue?.trigger_request_id === triggerRequestId) {
				return previousValue;
			}

			return { trigger_request_id: triggerRequestId };
		});
		setSelectedRunDetail(null);
		setDetailError('');
		setDetailLastCheckedAt(null);
		setDetailOpen(true);

		void Promise.all([
			fetchRunDetail(triggerRequestId, { showLoading: true }),
			loadRuns({}, { showLoading: false, syncSelectedRunId: triggerRequestId }),
		]);
	}, [activeTab, triggerRequestId]);

	useEffect(() => {
		if (!triggerRequestId) {
			return;
		}

		const matchedRun = runs.find((run) => run.trigger_request_id === triggerRequestId);
		if (!matchedRun) {
			return;
		}

		setSelectedRunSummary((previousValue) => (
			previousValue?.trigger_request_id === triggerRequestId
				? { ...previousValue, ...matchedRun }
				: previousValue
		));
	}, [runs, triggerRequestId]);

	function handleTabChange(_event, nextTab) {
		setActiveTab(nextTab);
		if (nextTab === 'token-usage') {
			setDetailOpen(false);
			navigate(tokenUsageRoute);
			return;
		}

		if (nextTab === 'maintenance') {
			setDetailOpen(false);
			navigate(maintenanceRoute);
			return;
		}

		navigate(forecastRunsRoute);
	}

	useEffect(() => {
		const triggerRequestId = selectedRunSummary?.trigger_request_id;
		if (!detailOpen || !triggerRequestId) {
			return undefined;
		}

		let isCancelled = false;
		let isInFlight = false;

		const refreshDetail = async () => {
			if (isCancelled || isInFlight) {
				return;
			}
			isInFlight = true;
			try {
				await Promise.all([
					fetchRunDetail(triggerRequestId),
					loadRuns({}, { showLoading: false, syncSelectedRunId: triggerRequestId }),
				]);
			} finally {
				isInFlight = false;
			}
		};

		const intervalId = window.setInterval(() => {
			refreshDetail();
		}, DETAIL_AUTO_REFRESH_MS);

		return () => {
			isCancelled = true;
			window.clearInterval(intervalId);
		};
	}, [detailOpen, selectedRunSummary?.trigger_request_id, clientCodeFilter, limit]);

	async function handleResubmit(run, event) {
		event?.stopPropagation?.();
		if (!run?.trigger_request_id || !run?.can_resubmit) {
			return;
		}

		if (!window.confirm(`Resubmit AI forecast run ${run.trigger_request_id}?`)) {
			return;
		}

		setResubmittingRunId(run.trigger_request_id);
		try {
			const response = await OrbitAiAdminService.resubmitForecastRun(run.trigger_request_id);
			tip.showSuccess(response?.message || 'AI forecast run resubmitted successfully.');
			await loadRuns();
			if (selectedRunSummary?.trigger_request_id === run.trigger_request_id) {
				await openRunDetail(run);
			}
		} catch (error) {
			console.log(error);
			tip.clear();
			tip.show(error?.response?.data?.message || 'Unable to resubmit this AI forecast run.', 5);
		} finally {
			setResubmittingRunId(null);
		}
	}

	async function handleRetryDag(run, event) {
		event?.stopPropagation?.();
		if (!run?.trigger_request_id || !run?.can_retry_dag) {
			return;
		}

		if (!window.confirm(`Retry the post-processing DAG for AI forecast run ${run.trigger_request_id}?`)) {
			return;
		}

		setRetryingDagRunId(run.trigger_request_id);
		try {
			const response = await OrbitAiAdminService.retryForecastRunDag(run.trigger_request_id);
			tip.showSuccess(response?.message || 'Post-processing DAG retry accepted.');
			await loadRuns();
			if (selectedRunSummary?.trigger_request_id === run.trigger_request_id) {
				await openRunDetail(run);
			}
		} catch (error) {
			console.log(error);
			tip.clear();
			tip.show(error?.response?.data?.message || 'Unable to retry post-processing for this AI forecast run.', 5);
		} finally {
			setRetryingDagRunId(null);
		}
	}

	async function handleReconcile(run, event) {
		event?.stopPropagation?.()
		if (!run?.trigger_request_id || !run?.can_reconcile) {
			return
		}

		if (!window.confirm(`Reconcile AI forecast run ${run.trigger_request_id}? This will clear stale superseded errors and refresh the run state.`)) {
			return
		}

		setReconcilingRunId(run.trigger_request_id)
		try {
			const response = await OrbitAiAdminService.reconcileForecastRun(run.trigger_request_id)
			tip.showSuccess(response?.message || 'Forecast run reconciliation completed.')
			await loadRuns()
			if (selectedRunSummary?.trigger_request_id === run.trigger_request_id) {
				await openRunDetail(run)
			}
		} catch (error) {
			console.log(error)
			tip.clear()
			tip.show(error?.response?.data?.message || 'Unable to reconcile this AI forecast run.', 5)
		} finally {
			setReconcilingRunId(null)
		}
	}

	async function handleNuclearReset(run, event) {
		event?.stopPropagation?.()
		if (!run?.trigger_request_id || !run?.can_nuclear_reset) {
			return
		}

		if (!window.confirm(`Apply Nuclear Reset to AI forecast run ${run.trigger_request_id}? This will force the run terminal, clear open errors for this request, and release the client for manual resubmission.`)) {
			return
		}

		setNuclearResettingRunId(run.trigger_request_id)
		try {
			const response = await OrbitAiAdminService.nuclearResetForecastRun(run.trigger_request_id)
			const hasFreshDetail = selectedRunSummary?.trigger_request_id === run.trigger_request_id && !!response?.detail?.run
			tip.showSuccess(response?.message || 'Nuclear reset applied. This run can now be resubmitted.')
			if (hasFreshDetail) {
				setSelectedRunDetail(response.detail)
				setSelectedRunSummary(response.detail.run)
				setDetailLastCheckedAt(new Date().toISOString())
			}
			await loadRuns({}, { showLoading: false, syncSelectedRunId: hasFreshDetail ? null : run.trigger_request_id })
			if (!hasFreshDetail && selectedRunSummary?.trigger_request_id === run.trigger_request_id) {
				await fetchRunDetail(run.trigger_request_id, { showLoading: false })
			}
		} catch (error) {
			console.log(error)
			tip.clear()
			tip.show(error?.response?.data?.message || 'Unable to apply a nuclear reset to this AI forecast run.', 5)
		} finally {
			setNuclearResettingRunId(null)
		}
	}

	const activeDetail = selectedRunDetail?.run || selectedRunSummary;
	const activeUsageSummary = selectedRunDetail?.usage_summary || null;
	const activeDagStatus = selectedRunDetail?.dag_status || null;
	const detailAutoRefreshLabel = detailLastCheckedAt
		? `Auto-refreshing every ${Math.floor(DETAIL_AUTO_REFRESH_MS / 1000)}s • syncing detail + run list • Last checked ${formatDateTime(detailLastCheckedAt)}`
		: `Auto-refreshing every ${Math.floor(DETAIL_AUTO_REFRESH_MS / 1000)}s while this dialog is open • syncing detail + run list`;
	const activePendingHelpText = activeDetail?.is_stale_pending_with_progress
		? `This run is older than ${activeDetail?.stale_pending_threshold_hours || 24} hours, has only partial progress, and is now treated as failed so it can be retried.`
		: activeDetail?.is_stale_pending
			? `This run exceeded the ${activeDetail?.stale_pending_threshold_hours || 24}-hour pending threshold without progress and is treated as failed.`
			: '';
	const activeRunningWithErrorsHelpText = activeDetail?.is_running_with_errors
		? getRunStatusDetailText(activeDetail)
		: '';
	const activeDagRetryHelpText = activeDetail?.can_retry_dag
		? 'Forecast rows were written successfully, but post-processing still needs a DAG retry. Use Retry DAG to re-trigger the downstream plan build without resubmitting the forecast run.'
		: '';
	const activeReconcileHelpText = activeDetail?.can_reconcile
		? 'Use Reconcile to clear stale superseded errors and refresh the run after recovered writes or a downstream DAG eventually completes.'
		: '';
	const activeNuclearResetHelpText = activeDetail?.can_nuclear_reset
		? `Nuclear Reset will force this run terminal, clear ${formatInteger(activeDetail?.open_error_count || 0)} open error(s), and release the client for manual resubmission. Use it only when you are confident this run is stuck and should be abandoned.`
		: '';
	const activeDagStatusText = toDisplayText(activeDagStatus?.label || activeDagStatus?.phase, '—');
	const activeDagStateText = toDisplayText(activeDagStatus?.state, '—');
	const activeDagStatusMessage = toDisplayText(activeDagStatus?.message, '');
	const activeOperatorState = getOperatorState(activeDetail);
	const activeOperatorInstruction = activeOperatorState.key === 'aborted'
		? 'Aborted — this run was cleared by operator nuclear reset. It is terminal, no new work was queued, and it is safe to submit a fresh replacement run if needed.'
		: activeOperatorState.key === 'in_progress'
		? activeDetail?.can_nuclear_reset
			? 'In Progress — wait if work is still active. If this run is clearly stuck, use Nuclear Reset to force terminal failure and release the client for resubmission.'
			: 'In Progress — do not resubmit yet. Wait for remaining forecast tasks to finish or for the run to become terminal.'
		: activeOperatorState.key === 'terminal_failure'
			? activeDetail?.can_nuclear_reset
				? 'Terminal Failure — review the latest blocking error. Use Nuclear Reset if the registry still has outstanding work and is blocking a fresh submission.'
				: 'Terminal Failure — safe to resubmit after reviewing the latest blocking error.'
			: activeOperatorState.key === 'completed'
				? 'Completed — forecast generation is finished. Review downstream DAG/result details only if follow-up action is needed.'
				: activeOperatorState.key === 'queued'
					? 'Queued — the run has been accepted and is waiting for worker execution.'
					: activeOperatorState.key === 'pending'
						? 'Pending — the run is still in progress. Review task counts, result rows, and DAG status for current progress.'
						: '';
	const activeRunStateSummary = activeDetail?.is_operator_nuclear_reset || activeDetail?.effective_status === 'aborted'
		? 'Aborted by operator • no work remains queued for this request'
		: activeDetail?.is_running_with_errors
		? `Still running • ${formatInteger(activeDetail?.pending_task_count || 0)} task(s) pending`
		: activeDetail?.registry_status === 'RUNNING'
			? `Registry still reports this run as running${activeDetail?.has_outstanding_work ? ` • ${formatInteger(activeDetail?.pending_task_count || 0)} task(s) pending` : ''}`
			: '';

	return (
		<Box sx={{ minWidth: '1080px' }}>
			<SectionTitle label="Orbit AI Admin" type="primary" customStyles={{ mt: '20px' }} />
			<Box sx={{ mt: 2, mb: 3 }}>
				<Typography variant="body1" color="text.secondary">
					Forecast Management surfaces profile-triggered AI forecast submissions, downstream failures, retry indicators, and one-click resubmission for failed runs. Token Usage consolidates cross-service usage visibility for Batch Forecasting, Daily Forecasting, and AI Chat Service, including client/location/location-class drilldown rows.
				</Typography>
			</Box>

			<Paper elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
				<Tabs
					value={activeTab}
					onChange={handleTabChange}
					indicatorColor="primary"
					textColor="primary"
				>
					<Tab label="Forecast Runs" value="forecast-runs" />
					<Tab label="Token Usage" value="token-usage" />
								<Tab label="Maintenance Banner" value="maintenance" />
				</Tabs>
			</Paper>

			{activeTab === 'forecast-runs' ? (
				<>
				<Grid container spacing={2} sx={{ mb: 3 }}>
				{SUMMARY_CARD_ORDER.map((status) => (
					<Grid item xs={12} sm={6} md={3} key={status}>
						<SummaryCard
							label={`${status.charAt(0).toUpperCase()}${status.slice(1)} Runs`}
							value={summaryCounts[status] || 0}
							color={STATUS_COLOR_MAP[status]}
						/>
					</Grid>
				))}
				</Grid>

				<Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} md={4}>
						<TextField
							fullWidth
							size="small"
							label="Client code filter"
							value={clientCodeFilter}
							onChange={(event) => setClientCodeFilter(event.target.value)}
							placeholder="e.g. DW130"
						/>
					</Grid>
					<Grid item xs={12} md={3}>
						<TextField
							select
							fullWidth
							size="small"
							label="Status"
							value={statusFilter}
							onChange={(event) => setStatusFilter(event.target.value)}
						>
							<MenuItem value="all">All statuses</MenuItem>
							<MenuItem value="aborted">Aborted</MenuItem>
							<MenuItem value="failed">Failed</MenuItem>
							<MenuItem value="pending">Pending</MenuItem>
							<MenuItem value="queued">Queued</MenuItem>
							<MenuItem value="completed">Completed</MenuItem>
						</TextField>
					</Grid>
					<Grid item xs={12} md={2}>
						<TextField
							select
							fullWidth
							size="small"
							label="Rows"
							value={String(limit)}
							onChange={(event) => setLimit(Number(event.target.value))}
						>
							{[50, 100, 150, 200].map((value) => (
								<MenuItem key={value} value={String(value)}>{value}</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12} md={3}>
						<Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
							<Button variant="contained" onClick={() => loadRuns()} disabled={isLoading}>
								Refresh
							</Button>
							<Button
								variant="outlined"
								onClick={() => {
									setClientCodeFilter('');
									setStatusFilter('all');
									setLimit(100);
									loadRuns({ clientCode: '', limit: 100 });
								}}
								disabled={isLoading}
							>
								Reset
							</Button>
						</Box>
					</Grid>
				</Grid>
				</Paper>

				{loadError ? <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert> : null}

				<Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
				<Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<Typography variant="h6">Forecast Runs</Typography>
					<Typography variant="body2" color="text.secondary">
						Showing {filteredRuns.length} of {runs.length} loaded runs
					</Typography>
				</Box>
				<Divider />
				{isLoading ? (
					<Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
						<CircularProgress />
					</Box>
				) : (
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Status</TableCell>
									<TableCell>Client</TableCell>
									<TableCell>Scope</TableCell>
									<TableCell>Trigger Request</TableCell>
									<TableCell>Tasks</TableCell>
									<TableCell>Retries</TableCell>
									<TableCell>Latest Error</TableCell>
									<TableCell>Updated</TableCell>
									<TableCell align="right">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredRuns.length === 0 ? (
									<TableRow>
										<TableCell colSpan={9} align="center" sx={{ py: 5 }}>
											No AI forecast runs matched the current filters.
										</TableCell>
									</TableRow>
								) : filteredRuns.map((run) => (
									<TableRow
										key={run.trigger_request_id}
										hover
										onClick={() => openRunDetail(run)}
										sx={{
											cursor: 'pointer',
											backgroundColor: run.effective_status === 'failed' ? 'rgba(211, 47, 47, 0.06)' : 'inherit',
										}}
									>
										<TableCell>
											<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
												<Chip size="small" color={STATUS_COLOR_MAP[run.effective_status] || 'default'} label={toDisplayText(run.effective_status_label)} sx={{ width: 'fit-content' }} />
												<Chip size="small" variant="outlined" color={OPERATOR_STATE_COLOR_MAP[getOperatorState(run).key] || 'default'} label={getOperatorState(run).label} sx={{ width: 'fit-content' }} />
												{run.is_running_with_errors && !run.is_operator_nuclear_reset ? (
													<Chip size="small" variant="outlined" color="warning" label="Still running" sx={{ width: 'fit-content' }} />
												) : null}
												{run.is_stale_pending_with_progress ? (
													<Chip size="small" variant="outlined" color="error" label="Stale partial run" sx={{ width: 'fit-content' }} />
												) : null}
												<Typography variant="caption" color="text.secondary">
													Registry: {run.registry_status || '—'}
												</Typography>
												{run.hours_since_activity !== null ? (
													<Typography variant="caption" color={run.is_stale_pending ? 'warning.main' : 'text.secondary'}>
														Last activity: {run.hours_since_activity}h ago
													</Typography>
												) : null}
											</Box>
										</TableCell>
										<TableCell>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>{run.client_code || '—'}</Typography>
											<Typography variant="caption" color="text.secondary">Client ID: {run.client_id || '—'}</Typography>
										</TableCell>
										<TableCell>{formatScope(run)}</TableCell>
										<TableCell>
											<Typography variant="body2" title={run.trigger_request_id || ''} sx={{ fontFamily: 'monospace', fontSize: 12 }}>
												{truncateText(run.trigger_request_id, 24)}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2">Published {run.published_task_count} / Completed {run.completed_task_count}</Typography>
											<Typography variant="caption" color={run.failed_task_count > 0 ? 'error.main' : 'text.secondary'}>
												Failed {run.failed_task_count} • Pending {run.pending_task_count}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2">Run retries: {run.retry_run_count}</Typography>
											<Typography variant="caption" color={run.pubsub_retry_count > 0 ? 'error.main' : 'text.secondary'}>
												Task retries: {run.pubsub_retry_count}
											</Typography>
										</TableCell>
										<TableCell title={getLatestErrorText(run)}>
											<Typography variant="body2" color={getLatestErrorText(run) !== '—' ? 'error.main' : 'text.secondary'}>
												{truncateText(getLatestErrorText(run), 80)}
											</Typography>
										</TableCell>
										<TableCell>{formatDateTime(run.updated_at)}</TableCell>
										<TableCell align="right">
											<Button size="small" onClick={(event) => { event.stopPropagation(); openRunDetail(run); }}>
												Inspect
											</Button>
											<Button
												size="small"
												onClick={(event) => handleReconcile(run, event)}
												disabled={!run.can_reconcile || reconcilingRunId === run.trigger_request_id}
											>
												{reconcilingRunId === run.trigger_request_id ? 'Reconciling…' : 'Reconcile'}
											</Button>
											<Button
												size="small"
												onClick={(event) => handleRetryDag(run, event)}
												disabled={!run.can_retry_dag || retryingDagRunId === run.trigger_request_id}
											>
												{retryingDagRunId === run.trigger_request_id ? 'Retrying DAG…' : 'Retry DAG'}
											</Button>
											<Button
												size="small"
												color="error"
												onClick={(event) => handleResubmit(run, event)}
												disabled={!run.can_resubmit || resubmittingRunId === run.trigger_request_id}
											>
												{resubmittingRunId === run.trigger_request_id ? 'Resubmitting…' : 'Resubmit'}
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
				</Paper>
				</>
			) : activeTab === 'token-usage' ? (
				<>
					<Grid container spacing={2} sx={{ mb: 3 }}>
						<Grid item xs={12} sm={6} md={3}>
							<SummaryCard label="Total Tokens" value={formatInteger(tokenUsageReport?.totals?.total_tokens || 0)} color="primary" />
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<SummaryCard label="Input Tokens" value={formatInteger(tokenUsageReport?.totals?.input_tokens || 0)} />
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<SummaryCard label="Output Tokens" value={formatInteger(tokenUsageReport?.totals?.output_tokens || 0)} />
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<SummaryCard label="Total Requests" value={formatInteger(tokenUsageReport?.totals?.request_count || 0)} />
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<SummaryCard
								label="Chat Token Coverage"
								value={`${Number(tokenUsageReport?.totals?.chat_coverage_percent || 0).toFixed(2)}%`}
							/>
						</Grid>
					</Grid>

					<Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
						<Grid container spacing={2} alignItems="center">
							<Grid item xs={12} md={4}>
								<TextField
									select
									fullWidth
									size="small"
									label="Window"
									value={String(tokenUsageDays)}
									onChange={(event) => setTokenUsageDays(Number(event.target.value))}
								>
									{[7, 14, 30, 60, 90].map((value) => (
										<MenuItem key={value} value={String(value)}>{`${value} days`}</MenuItem>
									))}
								</TextField>
							</Grid>
							<Grid item xs={12} md={8}>
								<Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
									<Button
										variant="contained"
										onClick={() => {
											loadTokenUsage();
											loadTokenUsageDrilldown();
										}}
										disabled={isTokenUsageLoading || isTokenUsageDrilldownLoading}
									>
										Refresh
									</Button>
									<Button
										variant="outlined"
										onClick={() => {
											setTokenUsageDays(30);
											loadTokenUsage({ days: 30 });
											loadTokenUsageDrilldown({ days: 30 });
										}}
										disabled={isTokenUsageLoading || isTokenUsageDrilldownLoading}
									>
										Reset
									</Button>
								</Box>
							</Grid>
						</Grid>
					</Paper>

					{tokenUsageError ? <Alert severity="error" sx={{ mb: 2 }}>{tokenUsageError}</Alert> : null}

					<Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
						<Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<Typography variant="h6">Token Usage</Typography>
							<Typography variant="body2" color="text.secondary">
								Window: last {tokenUsageReport?.window_days || tokenUsageDays} days
							</Typography>
						</Box>
						<Divider />
						{isTokenUsageLoading ? (
							<Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
								<CircularProgress />
							</Box>
						) : (
							<TableContainer>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Service</TableCell>
											<TableCell>Requests</TableCell>
											<TableCell>LLM Calls</TableCell>
											<TableCell>Input Tokens</TableCell>
											<TableCell>Output Tokens</TableCell>
											<TableCell>Total Tokens</TableCell>
											<TableCell>Coverage</TableCell>
											<TableCell>Notes</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{(tokenUsageReport?.services || []).length === 0 ? (
											<TableRow>
												<TableCell colSpan={8} align="center" sx={{ py: 5 }}>
													No token usage records available for the selected window.
												</TableCell>
											</TableRow>
										) : (tokenUsageReport?.services || []).map((serviceRow) => (
											<TableRow key={serviceRow.service_key}>
												<TableCell>
													<Typography variant="body2" sx={{ fontWeight: 600 }}>{toDisplayText(serviceRow.service_label, serviceRow.service_key)}</Typography>
													<Typography variant="caption" color="text.secondary">{toDisplayText(serviceRow.service_key)}</Typography>
												</TableCell>
												<TableCell>{formatInteger(serviceRow.request_count || 0)}</TableCell>
												<TableCell>{`${formatInteger(serviceRow.successful_call_count || 0)} success / ${formatInteger(serviceRow.attempted_call_count || 0)} attempted`}</TableCell>
												<TableCell>{formatInteger(serviceRow.input_tokens || 0)}</TableCell>
												<TableCell>{formatInteger(serviceRow.output_tokens || 0)}</TableCell>
												<TableCell>{formatInteger(serviceRow.total_tokens || 0)}</TableCell>
												<TableCell>
													{serviceRow.service_key === 'ai-chat-service'
														? `${Number(serviceRow.coverage_percent || 0).toFixed(2)}% (${formatInteger(serviceRow.rows_with_usage_count || 0)}/${formatInteger(serviceRow.request_count || 0)})`
														: 'n/a'}
												</TableCell>
												<TableCell title={serviceRow.notes || ''}>{truncateText(serviceRow.notes, 120)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Paper>

					<Paper elevation={1} sx={{ p: 2, mt: 3, mb: 2, borderRadius: 2 }}>
						<Grid container spacing={2} alignItems="center">
							<Grid item xs={12} md={4}>
								<TextField
									select
									fullWidth
									size="small"
									label="Drilldown Service"
									value={tokenUsageDrilldownServiceKey}
									onChange={(event) => setTokenUsageDrilldownServiceKey(event.target.value)}
								>
									<MenuItem value="daily_forecasting">Daily Forecasting</MenuItem>
									<MenuItem value="ai-chat-service">AI Chat Service</MenuItem>
								</TextField>
							</Grid>
							<Grid item xs={12} md={3}>
								<TextField
									select
									fullWidth
									size="small"
									label="Granularity"
									value={tokenUsageDrilldownGranularity}
									onChange={(event) => setTokenUsageDrilldownGranularity(event.target.value)}
								>
									<MenuItem value="client">Client</MenuItem>
									<MenuItem value="location">Location</MenuItem>
									<MenuItem value="location_class">Location Class</MenuItem>
								</TextField>
							</Grid>
							<Grid item xs={12} md={2}>
								<TextField
									select
									fullWidth
									size="small"
									label="Rows"
									value={String(tokenUsageDrilldownLimit)}
									onChange={(event) => setTokenUsageDrilldownLimit(Number(event.target.value))}
								>
									{[25, 50, 100, 200].map((value) => (
										<MenuItem key={value} value={String(value)}>{value}</MenuItem>
									))}
								</TextField>
							</Grid>
							<Grid item xs={12} md={3}>
								<Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
									<Button
										variant="contained"
										onClick={() => loadTokenUsageDrilldown()}
										disabled={isTokenUsageDrilldownLoading}
									>
										Load Drilldown
									</Button>
								</Box>
							</Grid>
						</Grid>
					</Paper>

					{tokenUsageDrilldownError ? <Alert severity="error" sx={{ mb: 2 }}>{tokenUsageDrilldownError}</Alert> : null}

					<Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
						<Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<Typography variant="h6">Token Usage Drilldown</Typography>
							<Typography variant="body2" color="text.secondary">
								{`Service: ${toDisplayText(tokenUsageDrilldown?.service_key, tokenUsageDrilldownServiceKey)} • Granularity: ${toDisplayText(tokenUsageDrilldown?.granularity, tokenUsageDrilldownGranularity)} • Top ${formatInteger(tokenUsageDrilldown?.filters?.limit || tokenUsageDrilldownLimit)} rows`}
							</Typography>
						</Box>
						<Divider />
						{toDisplayText(tokenUsageDrilldown?.notes, '') !== '' ? (
							<Alert severity="info" sx={{ borderRadius: 0 }}>
								{tokenUsageDrilldown.notes}
							</Alert>
						) : null}
						{isTokenUsageDrilldownLoading ? (
							<Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
								<CircularProgress />
							</Box>
						) : (
							<TableContainer>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Client</TableCell>
											<TableCell>Location</TableCell>
											<TableCell>Location Class</TableCell>
											<TableCell>Requests</TableCell>
											<TableCell>LLM Calls</TableCell>
											<TableCell>Input Tokens</TableCell>
											<TableCell>Output Tokens</TableCell>
											<TableCell>Total Tokens</TableCell>
											<TableCell>Coverage</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{(tokenUsageDrilldown?.rows || []).length === 0 ? (
											<TableRow>
												<TableCell colSpan={9} align="center" sx={{ py: 5 }}>
													No drilldown rows available for the selected filters.
												</TableCell>
											</TableRow>
										) : (tokenUsageDrilldown?.rows || []).map((row, index) => (
											<TableRow key={`${row.client_code || 'unknown'}-${row.location_id || 'none'}-${row.location_class_id || 'none'}-${index}`}>
												<TableCell>
													<Typography variant="body2" sx={{ fontWeight: 600 }}>{toDisplayText(row.client_code, 'unknown')}</Typography>
													<Typography variant="caption" color="text.secondary">Client ID: {toDisplayText(row.client_id)}</Typography>
												</TableCell>
												<TableCell>{toDisplayText(row.location_id)}</TableCell>
												<TableCell>{toDisplayText(row.location_class_id)}</TableCell>
												<TableCell>{formatInteger(row.request_count || 0)}</TableCell>
												<TableCell>{`${formatInteger(row.successful_call_count || 0)} success / ${formatInteger(row.attempted_call_count || 0)} attempted`}</TableCell>
												<TableCell>{formatInteger(row.input_tokens || 0)}</TableCell>
												<TableCell>{formatInteger(row.output_tokens || 0)}</TableCell>
												<TableCell>{formatInteger(row.total_tokens || 0)}</TableCell>
												<TableCell>{`${Number(row.coverage_percent || 0).toFixed(2)}% (${formatInteger(row.rows_with_usage_count || 0)}/${formatInteger(row.request_count || 0)})`}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Paper>
				</>
			) : (
				<>
					<Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							<Box>
								<Typography variant="h6">Ask Indie Maintenance Banner</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									Turn the in-app maintenance banner on or off for Ask Indie and set the exact message shown in the standard chat panel and page surfaces.
								</Typography>
							</Box>

							{maintenanceError ? <Alert severity="error">{maintenanceError}</Alert> : null}

							<Alert severity={maintenanceConfig.is_enabled ? 'warning' : 'info'}>
								{maintenanceConfig.is_enabled
									? 'The maintenance banner is currently visible to Ask Indie users.'
									: 'The maintenance banner is currently hidden from Ask Indie users.'}
							</Alert>

							<FormControlLabel
								control={(
									<Switch
										checked={maintenanceConfig.is_enabled}
										onChange={(event) => setMaintenanceConfig((previousValue) => ({
											...previousValue,
											is_enabled: event.target.checked,
										}))}
										color="warning"
									/>
								)}
								label={maintenanceConfig.is_enabled ? 'Banner enabled' : 'Banner disabled'}
							/>

							<TextField
								fullWidth
								multiline
								minRows={4}
								label="Maintenance message"
								placeholder="Example: Ask Indie will be unavailable from 10:00 PM to 10:30 PM CST while we move production traffic to the new AI platform services."
								value={maintenanceConfig.message}
								onChange={(event) => setMaintenanceConfig((previousValue) => ({
									...previousValue,
									message: event.target.value,
								}))}
								helperText={`${maintenanceConfig.message.length}/5000 characters`}
							/>

							{maintenanceConfig.message.trim() ? (
								<Alert severity="warning" sx={{ whiteSpace: 'pre-wrap' }}>
									{maintenanceConfig.message.trim()}
								</Alert>
							) : null}

							<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
								<Button
									variant="contained"
									onClick={handleSaveMaintenanceMessage}
									disabled={maintenanceLoading || maintenanceSaving || (maintenanceConfig.is_enabled && maintenanceConfig.message.trim() === '')}
								>
									{maintenanceSaving ? 'Saving…' : 'Save Banner'}
								</Button>
								<Button variant="outlined" onClick={loadMaintenanceMessage} disabled={maintenanceLoading || maintenanceSaving}>
									{maintenanceLoading ? 'Refreshing…' : 'Refresh'}
								</Button>
							</Box>

							<Typography variant="caption" color="text.secondary">
								Last updated: {formatDateTime(maintenanceConfig.updated_at)}
								{maintenanceConfig.updated_by_user_id ? ` • User ID ${maintenanceConfig.updated_by_user_id}` : ''}
							</Typography>
						</Box>
					</Paper>
				</>
			)}

			<Dialog open={detailOpen} onClose={closeRunDetail} fullWidth maxWidth="xl">
				<DialogTitle>
					<Box>
						<Typography variant="h6">Orbit AI Forecast Run Detail</Typography>
						{activeOperatorInstruction ? (
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								{activeOperatorInstruction}
							</Typography>
						) : null}
						<Typography variant="caption" color="text.secondary">
							{detailAutoRefreshLabel}
						</Typography>
					</Box>
				</DialogTitle>
				<DialogContent dividers>
					{detailLoading ? (
						<Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
							<CircularProgress />
						</Box>
					) : (
						<>
							{detailError ? <Alert severity="error" sx={{ mb: 2 }}>{detailError}</Alert> : null}
							{activeDetail ? (
								<>
									<Grid container spacing={2} sx={{ mb: 2 }}>
										<Grid item xs={12} md={3}>
											<Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
													<Chip size="small" color={STATUS_COLOR_MAP[activeDetail.effective_status] || 'default'} label={toDisplayText(activeDetail.effective_status_label)} />
													<Chip size="small" variant="outlined" color={OPERATOR_STATE_COLOR_MAP[activeOperatorState.key] || 'default'} label={activeOperatorState.label} />
													{activeDetail.is_running_with_errors && !activeDetail.is_operator_nuclear_reset ? <Chip size="small" variant="outlined" color="warning" label="Still running" /> : null}
													<Typography variant="body2" color="text.secondary">{formatScope(activeDetail)}</Typography>
												</Box>
												<RunStat label="Client" value={`${activeDetail.client_code || '—'} (${activeDetail.client_id || '—'})`} />
												<RunStat label="Trigger Request" value={activeDetail.trigger_request_id || '—'} />
												<RunStat label="Updated" value={formatDateTime(activeDetail.updated_at)} />
												{activeRunStateSummary ? <RunStat label="Execution State" value={activeRunStateSummary} color={activeDetail.is_running_with_errors ? 'warning.main' : 'text.primary'} /> : null}
											</Paper>
										</Grid>
										<Grid item xs={12} md={3}>
											<Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
												<RunStat label="Registry Status" value={activeDetail.registry_status || '—'} />
												<RunStat label="Expected / Published" value={`${activeDetail.expected_task_count} / ${activeDetail.published_task_count}`} />
												<RunStat label="Completed / Failed / Pending" value={`${activeDetail.completed_task_count} / ${activeDetail.failed_task_count} / ${activeDetail.pending_task_count}`} color={activeDetail.failed_task_count > 0 ? 'error.main' : 'text.primary'} />
												<RunStat label="Result Rows" value={`${activeDetail.result_count} total • ${activeDetail.processed_result_count} processed • ${activeDetail.pending_result_count} pending`} />
											</Paper>
										</Grid>
										<Grid item xs={12} md={3}>
											<Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
												<RunStat label="Run Retries" value={activeDetail.retry_run_count} />
												<RunStat label="Task Retries" value={activeDetail.pubsub_retry_count} color={activeDetail.pubsub_retry_count > 0 ? 'error.main' : 'text.primary'} />
												<RunStat label="Open Errors" value={activeDetail.open_error_count} color={activeDetail.open_error_count > 0 ? 'error.main' : 'text.primary'} />
												<RunStat label="Latest Error" value={getLatestErrorText(activeDetail)} color={getLatestErrorText(activeDetail) !== '—' ? 'error.main' : 'text.primary'} />
													<RunStat label="Last Activity" value={activeDetail.hours_since_activity !== null ? `${activeDetail.hours_since_activity}h ago` : '—'} color={activeDetail.is_stale_pending ? 'warning.main' : 'text.primary'} />
											</Paper>
										</Grid>
										<Grid item xs={12} md={3}>
											<Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
												<RunStat label="LLM Total Tokens" value={formatInteger(activeUsageSummary?.total_tokens || 0)} />
												<RunStat label="LLM Input / Output" value={`${formatInteger(activeUsageSummary?.input_tokens || 0)} / ${formatInteger(activeUsageSummary?.output_tokens || 0)}`} />
												<RunStat label="LLM Calls" value={`${formatInteger(activeUsageSummary?.successful_call_count || 0)} success • ${formatInteger(activeUsageSummary?.attempted_call_count || 0)} attempted`} />
												<RunStat label="Models" value={(activeUsageSummary?.models || []).length > 0 ? activeUsageSummary.models.join(', ') : '—'} />
											</Paper>
										</Grid>
									</Grid>

									{activeRunningWithErrorsHelpText ? (
										<Alert severity="warning" sx={{ mb: 2 }}>
											{activeRunningWithErrorsHelpText}
										</Alert>
									) : null}

									{activePendingHelpText ? (
										<Alert severity="error" sx={{ mb: 2 }}>
											{activePendingHelpText}
										</Alert>
									) : null}

									{activeDagRetryHelpText ? (
										<Alert severity="warning" sx={{ mb: 2 }}>
											{activeDagRetryHelpText}
										</Alert>
									) : null}

									{activeReconcileHelpText ? (
										<Alert severity="info" sx={{ mb: 2 }}>
											{activeReconcileHelpText}
										</Alert>
									) : null}

									{activeNuclearResetHelpText ? (
										<Alert severity="warning" sx={{ mb: 2 }}>
											{activeNuclearResetHelpText}
										</Alert>
									) : null}

									<Paper variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
										<Box sx={{ px: 2, py: 1.5 }}>
											<Typography variant="h6">Post-Processing DAG</Typography>
										</Box>
										<Divider />
										<Box sx={{ p: 2 }}>
											<Grid container spacing={2}>
												<Grid item xs={12} md={3}>
													<RunStat
														label="Composer Status"
														value={<Chip size="small" color={DAG_PHASE_COLOR_MAP[activeDagStatus?.phase] || 'default'} label={activeDagStatusText} />}
													/>
												</Grid>
												<Grid item xs={12} md={3}>
													<RunStat label="DAG Run ID" value={activeDagStatus?.dag_run_id || '—'} />
												</Grid>
												<Grid item xs={12} md={3}>
													<RunStat label="Airflow State" value={activeDagStateText} />
												</Grid>
												<Grid item xs={12} md={3}>
													<RunStat label="Logical Date" value={formatDateTime(activeDagStatus?.logical_date)} />
												</Grid>
												<Grid item xs={12} md={6}>
													<RunStat label="Started" value={formatDateTime(activeDagStatus?.start_date)} />
												</Grid>
												<Grid item xs={12} md={6}>
													<RunStat label="Ended" value={formatDateTime(activeDagStatus?.end_date)} />
												</Grid>
											</Grid>
											{activeDagStatusMessage ? (
												<Alert severity={activeDagStatus?.phase === 'failed' ? 'error' : 'info'} sx={{ mt: 2 }}>
													{activeDagStatusMessage}
												</Alert>
											) : null}
											{activeDagStatus?.trigger_errors && Array.isArray(activeDagStatus.trigger_errors) && activeDagStatus.trigger_errors.length > 0 ? (
												<Box sx={{ mt: 2 }}>
													<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
														DAG Trigger Errors ({activeDagStatus.trigger_errors.length})
													</Typography>
													<Box sx={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 1, p: 1.5 }}>
														{activeDagStatus.trigger_errors.map((error, idx) => (
															<Box key={idx} sx={{ mb: idx < activeDagStatus.trigger_errors.length - 1 ? 1 : 0 }}>
																<Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
																	{formatDateTime(error.created_at)}
																</Typography>
																<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
																	{error.error_message}
																</Typography>
															</Box>
														))}
													</Box>
												</Box>
											) : null}
										</Box>
									</Paper>

									<Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
										<Box sx={{ px: 2, py: 1.5 }}>
											<Typography variant="h6">Recent Result Rows</Typography>
										</Box>
										<Divider />
										<TableContainer>
											<Table size="small">
												<TableHead>
													<TableRow>
														<TableCell>Location Class</TableCell>
														<TableCell>Status</TableCell>
														<TableCell>Processing Time (s)</TableCell>
															<TableCell>LLM Tokens</TableCell>
														<TableCell>Created</TableCell>
														<TableCell>Plan Data Preview</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{(selectedRunDetail?.results || []).length === 0 ? (
														<TableRow>
																<TableCell colSpan={6} align="center">No result rows found for this run.</TableCell>
														</TableRow>
													) : (selectedRunDetail?.results || []).map((result) => (
														<TableRow key={result.id || `${result.location_class_id}-${result.created_at}`}>
															<TableCell>{result.location_class_id || '—'}</TableCell>
															<TableCell>
																<Chip size="small" label={toDisplayText(result.status)} color={String(result.status || '').toLowerCase() === 'failed' ? 'error' : String(result.status || '').toLowerCase() === 'processed' ? 'success' : 'warning'} />
															</TableCell>
															<TableCell>{result.processing_time_seconds ?? '—'}</TableCell>
																<TableCell title={(result.llm_models || []).join(', ')}>
																	<Typography variant="body2">{formatInteger(result.llm_total_tokens || 0)}</Typography>
																	<Typography variant="caption" color="text.secondary">
																		{`${formatInteger(result.llm_successful_call_count || 0)} success • ${formatInteger(result.llm_attempted_call_count || 0)} attempted`}
																	</Typography>
																</TableCell>
															<TableCell>{formatDateTime(result.created_at)}</TableCell>
															<TableCell title={result.plan_data_json || ''}>{truncateText(result.plan_data_json, 120)}</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									</Paper>

									<Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
										<Box sx={{ px: 2, py: 1.5 }}>
											<Typography variant="h6">Error Log Detail</Typography>
										</Box>
										<Divider />
										<TableContainer>
											<Table size="small">
												<TableHead>
													<TableRow>
														<TableCell>Stage</TableCell>
														<TableCell>Message</TableCell>
														<TableCell>Delivery Attempt</TableCell>
														<TableCell>Location Class</TableCell>
														<TableCell>Created</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{(selectedRunDetail?.errors || []).length === 0 ? (
														<TableRow>
															<TableCell colSpan={5} align="center">No error rows found for this run.</TableCell>
														</TableRow>
													) : (selectedRunDetail?.errors || []).map((errorRow) => (
														<TableRow key={errorRow.id || `${errorRow.error_stage}-${errorRow.created_at}`}>
															<TableCell>
																<Chip size="small" color="error" label={toDisplayText(errorRow.error_stage, 'unknown')} />
															</TableCell>
															<TableCell title={errorRow.error_message || ''}>{truncateText(errorRow.error_message, 120)}</TableCell>
															<TableCell>{errorRow.delivery_attempt ?? '—'}</TableCell>
															<TableCell>{errorRow.location_class_id || errorRow.requested_location_class_id || '—'}</TableCell>
															<TableCell>{formatDateTime(errorRow.created_at)}</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									</Paper>

									{(selectedRunDetail?.errors || []).slice(0, 1).map((errorRow) => (
										<Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }} key={errorRow.id || 'error-json-preview'}>
											<Typography variant="h6" sx={{ mb: 1 }}>
												Latest Error Payload Preview
											</Typography>
											<JsonPreview label="Task Payload" value={errorRow.task_payload_json} />
											<JsonPreview label="Forecast Response" value={errorRow.forecast_response_json} />
										</Paper>
									))}

									{(selectedRunDetail?.related_runs || []).length > 0 ? (
										<Paper variant="outlined" sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
											<Box sx={{ px: 2, py: 1.5 }}>
												<Typography variant="h6">Related Prior Runs</Typography>
											</Box>
											<Divider />
											<TableContainer>
												<Table size="small">
													<TableHead>
														<TableRow>
															<TableCell>Status</TableCell>
															<TableCell>Trigger Request</TableCell>
															<TableCell>Tasks</TableCell>
															<TableCell>Updated</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{selectedRunDetail.related_runs.map((relatedRun) => (
															<TableRow key={relatedRun.trigger_request_id}>
																<TableCell>
																										<Chip size="small" color={STATUS_COLOR_MAP[relatedRun.effective_status] || 'default'} label={toDisplayText(relatedRun.effective_status_label)} />
																</TableCell>
																<TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }} title={relatedRun.trigger_request_id || ''}>
																	{truncateText(relatedRun.trigger_request_id, 28)}
																</TableCell>
																<TableCell>{`${relatedRun.completed_task_count}/${relatedRun.published_task_count} complete • ${relatedRun.failed_task_count} failed`}</TableCell>
																<TableCell>{formatDateTime(relatedRun.updated_at)}</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</TableContainer>
										</Paper>
									) : null}
								</>
							) : (
								<Typography color="text.secondary">Select a forecast run to inspect its details.</Typography>
							)}
						</>
					)}
				</DialogContent>
				<DialogActions>
					{activeDetail?.can_nuclear_reset ? (
						<Button
							color="warning"
							onClick={(event) => handleNuclearReset(activeDetail, event)}
							disabled={nuclearResettingRunId === activeDetail.trigger_request_id}
						>
							{nuclearResettingRunId === activeDetail.trigger_request_id ? 'Resetting…' : 'Nuclear Reset'}
						</Button>
					) : null}
					{activeDetail?.can_reconcile ? (
						<Button
							onClick={(event) => handleReconcile(activeDetail, event)}
							disabled={reconcilingRunId === activeDetail.trigger_request_id}
						>
							{reconcilingRunId === activeDetail.trigger_request_id ? 'Reconciling…' : 'Reconcile'}
						</Button>
					) : null}
					{activeDetail?.can_retry_dag ? (
						<Button
							onClick={(event) => handleRetryDag(activeDetail, event)}
							disabled={retryingDagRunId === activeDetail.trigger_request_id}
						>
							{retryingDagRunId === activeDetail.trigger_request_id ? 'Retrying DAG…' : 'Retry DAG'}
						</Button>
					) : null}
					{activeDetail?.can_resubmit ? (
						<Button
							color="error"
							onClick={(event) => handleResubmit(activeDetail, event)}
							disabled={resubmittingRunId === activeDetail.trigger_request_id}
						>
							{resubmittingRunId === activeDetail.trigger_request_id ? 'Resubmitting…' : 'Resubmit Failed Run'}
						</Button>
					) : null}
					<Button onClick={closeRunDetail}>Close</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default OrbitAiAdmin;
